/**
 * lightningPipeline — dramatic lightning bolts with branches.
 *
 * A pixel-perfect rendering of fractal lightning bolts. Each bolt
 * has a main path of N zigzagging segments, with optional sub-branches
 * splitting off recursively (unrolled here for WebGL2).
 *
 * We render MULTIPLE bolts and a glowing cloud backdrop.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface LightningPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

// Procedural lightning bolt via a noise-driven path.
const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

// Value noise.
float vnoise(vec2 p) {
  vec2 ip = floor(p);
  vec2 fp = fract(p);
  float a = hash21(ip);
  float b = hash21(ip + vec2(1.0, 0.0));
  float c = hash21(ip + vec2(0.0, 1.0));
  float d = hash21(ip + vec2(1.0, 1.0));
  vec2 u = fp * fp * (3.0 - 2.0 * fp);
  return mix(mix(a, b, u.x), mix(c, d, u.x), u.y) * 2.0 - 1.0;
}

float fbm(vec2 p) {
  float v = 0.0;
  float amp = 0.5;
  for (int i = 0; i < 4; i++) {
    v += amp * vnoise(p);
    p *= 2.0;
    amp *= 0.5;
  }
  return v;
}

// Distance from a point to a line segment ab.
float distToSeg(vec2 p, vec2 a, vec2 b) {
  vec2 pa = p - a;
  vec2 ba = b - a;
  float h = clamp(dot(pa, ba) / max(dot(ba, ba), 1e-4), 0.0, 1.0);
  return length(pa - ba * h);
}

// Single zig-zag bolt path starting from 'start', going to 'end' with N segments.
// Returns the minimum distance to any segment.
float boltPath(vec2 p, vec2 start, vec2 end, int segs, float seed) {
  float minD = 1e3;
  vec2 a = start;
  for (int i = 1; i <= 24; i++) {
    if (i > segs) break;
    // Compute endpoint with jitter perpendicular to the segment axis.
    float t = float(i) / float(segs);
    vec2 base = mix(start, end, t);
    float jitter = 0.08;
    // Add perpendicular jitter that varies with i.
    vec2 perp = vec2(-(end.y - start.y), end.x - start.x);
    float perpLen = length(perp);
    perp = perpLen > 0.0001 ? perp / perpLen : vec2(0.0, 1.0);
    float j = (hash21(vec2(seed, float(i))) - 0.5) * 2.0 * jitter * (1.0 - t);
    vec2 b = base + perp * j;
    minD = min(minD, distToSeg(p, a, b));
    a = b;
  }
  return minD;
}

// Main trunk of a bolt, with sub-branches splitting off at midpoint.
float boltTree(vec2 p, vec2 start, vec2 end, float seed) {
  // Main trunk: 12 segments.
  float mainD = boltPath(p, start, end, 12, seed);
  // Sub-branch 1: from 30% to 80% of the trunk, going off to the side.
  vec2 mid1 = mix(start, end, 0.3);
  vec2 mid2 = mix(start, end, 0.8);
  vec2 perp = vec2(-(end.y - start.y), end.x - start.y);
  float perpLen = length(perp);
  perp = perpLen > 0.0001 ? perp / perpLen : vec2(0.0, 1.0);
  vec2 branchEnd1 = mid1 + perp * 0.4 + vec2(0.0, 0.3);
  vec2 branchEnd2 = mid2 + perp * -0.35 + vec2(0.0, -0.2);
  float b1 = boltPath(p, mid1, branchEnd1, 6, seed + 11.0);
  float b2 = boltPath(p, mid2, branchEnd2, 6, seed + 23.0);
  // Sub-sub-branches.
  vec2 sb1Start = mix(mid1, branchEnd1, 0.4);
  vec2 sb1End = sb1Start + perp * 0.15;
  float sb1 = boltPath(p, sb1Start, sb1End, 3, seed + 31.0);
  vec2 sb2Start = mix(mid2, branchEnd2, 0.5);
  vec2 sb2End = sb2Start - perp * 0.18;
  float sb2 = boltPath(p, sb2Start, sb2End, 3, seed + 41.0);
  return min(mainD, min(min(b1, b2), min(sb1, sb2)));
}

// Storm cloud backdrop.
float clouds(vec2 p, float t) {
  float v = 0.0;
  v += fbm(p * 1.5 + vec2(0.0, t * 0.05));
  v += 0.6 * fbm(p * 3.0 - vec2(t * 0.02, 0.0));
  v += 0.3 * fbm(p * 6.0 + vec2(t * 0.04, 0.0));
  return v;
}

void main() {
  float bassAvg = (uBass.x + uBass.y + uBass.z) / 3.0;
  float midAvg = (uMid.x + uMid.y + uMid.z) / 3.0;
  float highAvg = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bassAvg = pow(clamp(bassAvg * 2.5, 0.0, 1.0), 0.7);
  midAvg = pow(clamp(midAvg * 3.0, 0.0, 1.0), 0.7);
  highAvg = pow(clamp(highAvg * 4.0, 0.0, 1.0), 0.7);

  // Aspect-corrected coords with Y up.
  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);
  p.y = -p.y;

  // Storm cloud backdrop — multi-octave noise.
  vec2 cloudUv = p * 1.2;
  float cloud = clouds(cloudUv, uTime);
  cloud = smoothstep(-0.4, 0.8, cloud);
  // Cloud color: deep gray-purple base, brighter at lightning centers.
  vec3 cloudShadow = vec3(0.04, 0.02, 0.08);
  vec3 cloudBright = vec3(0.25, 0.20, 0.30);
  vec3 col = mix(cloudShadow, cloudBright, cloud);
  // Bottom darkening — storms are dark at the bottom.
  col *= mix(0.4, 1.0, smoothstep(-1.0, 0.5, p.y));

  // Lightning: 5 bolts, each with a flash cycle.
  vec3 boltColor = vec3(0.0);
  for (int b = 0; b < 5; b++) {
    float fb = float(b);
    float seed = fb * 17.31 + 1.7;
    // Each bolt has its own time phase.
    float flashPeriod = 0.8 + 0.4 * hash21(vec2(fb, 1.7));
    float flashPhase = mod(uTime + fb * 0.37, flashPeriod);
    // Pulse: bright with periodic dim.
    float flashIntensity = 0.3 + 0.7 * exp(-flashPhase * 5.0);
    // Bolt position varies with time.
    float xOffset = (hash21(vec2(uTime * 0.3 + fb, 5.3)) - 0.5) * 1.6;
    vec2 start = vec2(xOffset, 0.9 + 0.05 * sin(uTime * 2.0 + fb));
    vec2 end = vec2(xOffset + (hash21(vec2(fb, 7.1)) - 0.5) * 0.4, -0.7);
    float d = boltTree(p, start, end, seed);
    // Glow intensity drops with distance squared.
    float glow = exp(-d * d * 1500.0);
    float hotCore = exp(-d * d * 15000.0);
    // Hot white core, blue-purple halo.
    vec3 coreColor = vec3(1.0, 1.0, 0.95);
    vec3 haloColor = vec3(0.45, 0.55, 1.0);
    vec3 thisBolt = mix(haloColor, coreColor, hotCore);
    boltColor += thisBolt * (glow + hotCore * 2.0) * flashIntensity;
  }
  // Additive blend of bolts onto the storm.
  col += boltColor * 2.5;
  // Lightning flashes light up the clouds — overall brightness pulse on bass.
  col *= 1.0 + bassAvg * 0.3;
  // Add magenta tint to clouds during heavy bass.
  col = mix(col, col + vec3(0.5, 0.2, 0.4) * bassAvg * 0.5 * cloud, 0.5);

  // Vignette.
  vec2 vp = (gl_FragCoord.xy - uResolution * 0.5) / uResolution.y;
  float vig = 1.0 - dot(vp, vp) * 0.4;
  col *= clamp(vig, 0.0, 1.0);

  // Tone mapping.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  outColor = vec4(col, 1.0);
}
`;

export function createLightningPipeline(gl: WebGL2RenderingContext): LightningPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    bands: new Float32Array(9),
  };
}

export function drawLightning(
  gl: WebGL2RenderingContext,
  pipe: LightningPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}

export function computeLightningBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.7 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.0 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.2 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.4 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.7 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.1 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.5 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.0 + 3.0);
}

export function computeLightningRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}
